import { FC } from 'react';
import { Box, Stack, Theme } from '@mui/material';
import MuiCheckbox from '@mui/material/Checkbox';
import classNames from 'classnames';
import { createUseStyles } from 'react-jss';
import { OrgMode, OrgModeOptions } from '@/lib/models/org.model';
import {
  PartnerOrgAction,
  PartnerOrgActionLabels,
  PartnerOrgActions,
  PartnerOrgModeSettings,
} from '@/lib/models/partner.model';
import Text from '@/components/text';
import Icon from '@/components/icon';
import Switch from '@/components/form/switch';
import StatusChip from '@/components/chip/status-chip';

type OrgModes = Record<OrgMode, PartnerOrgModeSettings>;

interface Props {
  orgModes: OrgModes;
  // When present the matrix is editable; without it the matrix reads back what is granted.
  onChange?: (mode: OrgMode, key: keyof PartnerOrgModeSettings, value: boolean) => void;
}

const useStyles = createUseStyles((theme: Theme) => ({
  matrix: {
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
    overflow: 'hidden',
  },
  row: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1fr) repeat(3, 76px)',
    alignItems: 'center',
    padding: '4px 12px',
    borderTop: `1px solid ${theme.palette.divider}`,
    '&.header': {
      borderTop: 'none',
      backgroundColor: theme.palette.action.hover,
      padding: '8px 12px',
    },
    '&.readonly': {
      padding: '10px 12px',
    },
  },
  cell: {
    display: 'flex',
    justifyContent: 'center',
  },
  seedRow: {
    padding: '6px 12px 10px 28px',
    '& .MuiFormControl-root': {
      margin: 0,
    },
  },
  checkbox: {
    padding: 4,
    '&:hover': {
      backgroundColor: 'inherit',
    },
    '& svg.MuiSvgIcon-root': {
      opacity: 0.8,
    },
  },
}));

const OrgModesMatrix: FC<Props> = ({ orgModes, onChange }) => {
  const styles = useStyles();
  const editable = !!onChange;
  const demo = orgModes.demo;
  const showSeedRow = editable || demo.create;

  const renderCell = (mode: OrgMode, modeLabel: string, action: PartnerOrgAction) => {
    const granted = orgModes[mode]?.[action] === true;

    if (!editable) {
      return granted ? (
        <Icon name="check" size="small" color="success" />
      ) : (
        <Text color="light-grey">–</Text>
      );
    }

    return (
      <MuiCheckbox
        checked={granted}
        size="small"
        color="primary"
        disableRipple
        className={styles.checkbox}
        inputProps={{ 'aria-label': `${modeLabel}: ${PartnerOrgActionLabels[action]}` }}
        onChange={(_event, checked) => {
          onChange(mode, action, checked);
          if (mode === 'demo' && action === 'create' && !checked) {
            onChange('demo', 'seedData', false);
          }
        }}
      />
    );
  };

  return (
    <Box className={styles.matrix}>
      <Box className={classNames(styles.row, 'header')}>
        <Text size="small" color="grey">
          Mode
        </Text>
        {PartnerOrgActions.map((action) => (
          <Box key={action} className={styles.cell}>
            <Text size="small" color="grey">
              {PartnerOrgActionLabels[action]}
            </Text>
          </Box>
        ))}
      </Box>

      {OrgModeOptions.map(({ label, value }) => {
        const mode = value as OrgMode;
        const modeLabel = String(label);

        return (
          <Box key={mode}>
            <Box className={classNames(styles.row, { readonly: !editable })}>
              <Text color="black">{modeLabel}</Text>
              {PartnerOrgActions.map((action) => (
                <Box key={action} className={styles.cell}>
                  {renderCell(mode, modeLabel, action)}
                </Box>
              ))}
            </Box>

            {mode === 'demo' && showSeedRow && (
              <Stack
                direction="row"
                alignItems="center"
                justifyContent="space-between"
                gap={2}
                className={styles.seedRow}
              >
                <Box>
                  <Text size="small" color="black">
                    Seed demo data
                  </Text>
                  <Text size="x-small" color="grey">
                    Fill each new demo organization with the generated dataset.
                  </Text>
                </Box>
                {editable ? (
                  <Switch
                    name="features.orgModes.demo.seedData"
                    checked={demo.seedData}
                    disabled={!demo.create}
                    fullWidth={false}
                    onChange={(checked) => onChange('demo', 'seedData', checked)}
                  />
                ) : (
                  <StatusChip value={demo.seedData ? 'enabled' : 'disabled'} size="small" />
                )}
              </Stack>
            )}
          </Box>
        );
      })}
    </Box>
  );
};

export default OrgModesMatrix;
